import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useApi, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Button,
  Tooltip,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Collapse,
  Avatar,
  LinearProgress,
  MenuItem,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import GroupIcon from '@material-ui/icons/Group';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import UpdateIcon from '@material-ui/icons/Update';
import CategoryIcon from '@material-ui/icons/Category';
import DateRangeIcon from '@material-ui/icons/DateRange';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import VisibilityIcon from '@material-ui/icons/Visibility';
import PersonIcon from '@material-ui/icons/Person';
import AssessmentIcon from '@material-ui/icons/AssessmentOutlined';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import BuildIcon from '@material-ui/icons/Build';
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import { useNavigate } from 'react-router-dom';
// TODO: Revert to normal ESM import once recharts v3 ships with proper ESM support.
// Import from CJS build to avoid webpack 5 ESM HMR incompatibility with recharts.
// eslint-disable-next-line @typescript-eslint/no-require-imports
const {
  ResponsiveContainer, PieChart, Pie, Cell,
  Tooltip: RTooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList,
} = require('recharts/lib/index.js') as typeof import('recharts');

import { MarketplaceProvider, useMarketplace } from './MarketplaceContext';
import { MarketplaceCard } from './MarketplaceCard';
import { TYPE_META } from './typeMetadata';
import { TypeIcon } from './TypeIcon';
import { MarketplaceItem, ItemType } from './types';
import { MARKETPLACE_ADMIN_EMAILS } from './marketplace-admins';
import { useMarketplaceFetchApi } from './marketplaceFetch';

/* ═══════════════════════════════════════════════════════════════════════════
   Tokens
   ═══════════════════════════════════════════════════════════════════════════ */
const T = {
  pageBg:      '#f0f2f9',
  surface:     '#ffffff',
  surfaceAlt:  '#f8f9fc',
  border:      'rgba(0,0,0,.06)',
  borderMid:   'rgba(0,0,0,.10)',
  shadow:      '0 1px 3px rgba(0,0,0,.06), 0 1px 2px rgba(0,0,0,.04)',
  shadowMd:    '0 4px 16px rgba(0,0,0,.10)',
  shadowLg:    '0 8px 32px rgba(0,0,0,.12)',
  text:        '#0f172a',
  textSec:     '#64748b',
  textDim:     '#94a3b8',
  accent:      '#4f46e5',
  /* hero gradient — matches AI Marketplace page */
  heroA:       '#061A38',
  heroB:       '#0D3E86',
  heroC:       '#0057BF',
  heroD:       '#3D8EDB',
  /* status */
  emerald:     '#10b981', emeraldSoft: 'rgba(16,185,129,.1)', emeraldText: '#065f46',
  amber:       '#f59e0b', amberSoft:   'rgba(245,158,11,.1)',  amberText:   '#78350f',
  sky:         '#0ea5e9', skySoft:     'rgba(14,165,233,.1)',  skyText:     '#0c4a6e',
  violet:      '#8b5cf6', violetSoft:  'rgba(139,92,246,.1)',  violetText:  '#4c1d95',
  rose:        '#f43f5e', roseSoft:    'rgba(244,63,94,.1)',   roseText:    '#881337',
  orange:      '#f97316', orangeSoft:  'rgba(249,115,22,.1)',  orangeText:  '#7c2d12',
  indigo:      '#4f46e5', indigoSoft:  'rgba(79,70,229,.1)',   indigoText:  '#312e81',
} as const;

/* per-section accent colours */
const SEC = {
  newItems:    { bar: T.emerald, headerBg: 'rgba(16,185,129,.04)',  dot: '#10b981' },
  updated:     { bar: T.amber,   headerBg: 'rgba(245,158,11,.04)',  dot: '#f59e0b' },
  contribs:    { bar: T.sky,     headerBg: 'rgba(14,165,233,.04)',  dot: '#0ea5e9' },
  downloads:   { bar: T.violet,  headerBg: 'rgba(139,92,246,.04)', dot: '#8b5cf6' },
  log:         { bar: T.rose,    headerBg: 'rgba(244,63,94,.04)',   dot: '#f43f5e' },
  visitors:    { bar: '#0891b2', headerBg: 'rgba(8,145,178,.04)',   dot: '#06b6d4' },
  reactions:   { bar: '#e11d48', headerBg: 'rgba(225,29,72,.04)',   dot: '#f43f5e' },
} as const;

/* KPI definitions */
const KPI_DEF = [
  { key: 'total',   icon: CategoryIcon,          grad: 'linear-gradient(135deg,#4f46e5,#6366f1)', glow: 'rgba(79,70,229,.35)' },
  { key: 'new',     icon: AddCircleOutlineIcon,   grad: 'linear-gradient(135deg,#059669,#10b981)', glow: 'rgba(16,185,129,.35)' },
  { key: 'updated', icon: UpdateIcon,             grad: 'linear-gradient(135deg,#d97706,#f59e0b)', glow: 'rgba(245,158,11,.35)' },
  { key: 'contrib', icon: GroupIcon,              grad: 'linear-gradient(135deg,#0284c7,#0ea5e9)', glow: 'rgba(14,165,233,.35)' },
  { key: 'dl',      icon: CloudDownloadIcon,      grad: 'linear-gradient(135deg,#7c3aed,#8b5cf6)', glow: 'rgba(139,92,246,.35)' },
  { key: 'visitors', icon: VisibilityIcon,          grad: 'linear-gradient(135deg,#0891b2,#06b6d4)', glow: 'rgba(6,182,212,.35)'  },
] as const;

const AVATAR_PALETTE = ['#4f46e5','#10b981','#f59e0b','#0ea5e9','#f43f5e','#f97316'];
const pickColor = (s: string) => AVATAR_PALETTE[s.charCodeAt(0) % AVATAR_PALETTE.length];

const MARKETPLACE_TYPES = Object.keys(TYPE_META) as ItemType[];
const PRESETS: { label: string; days: number | null }[] = [
  { label: 'Today', days: 1 }, { label: '3d', days: 3 }, { label: '7d', days: 7 },
  { label: '14d', days: 14 }, { label: '30d', days: 30 }, { label: 'All', days: null },
];
const MAX_CARDS = 9;
const REACTIONS_CHART_LIMIT = 8; // chart shows top N; backend retains every reaction

/* ═══════════════════════════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════════════════════════ */
const isoDate   = (d: Date) => d.toISOString().split('T')[0];
const ms        = (s: string) => new Date(s).getTime();
const msEnd     = (s: string) => ms(s) + 86_400_000 - 1;
const fmt       = (s: string) => new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const ago       = (n: number) => isoDate(new Date(Date.now() - n * 86_400_000));
const today     = () => isoDate(new Date());
const fmtShort  = (s: string) => new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const cleanName = (a: string) => a.replace(/_verint$/i, '').replace(/[-_.]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const initials  = (n: string) => n.split(' ').slice(0, 2).map(w => w[0] ?? '').join('').toUpperCase();

function sortByField(list: MarketplaceItem[], field: 'createdAt' | 'updatedAt') {
  return [...list].sort((a, b) => {
    const da = a[field] ?? ''; const db = b[field] ?? '';
    return da && db ? db.localeCompare(da) : da ? -1 : db ? 1 : 0;
  });
}

interface Contrib { action: 'Added' | 'Edited' | 'Maintenance'; type: string; item: string; author: string; date: string; ts: number }
interface Person  { name: string; added: number; edited: number; maint: number; total: number; rank: number }

/* ═══════════════════════════════════════════════════════════════════════════
   Table mixin  — LegalInspect style
   ═══════════════════════════════════════════════════════════════════════════ */
const TBL: Record<string, any> = {
  width: '100%',
  '& .MuiTableCell-root': {
    padding: '12px 18px', fontSize: '0.8125rem', color: T.text,
    borderBottom: `1px solid ${T.border}`, background: 'transparent',
    verticalAlign: 'middle', letterSpacing: '-0.01em',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontWeight: 500, fontSize: '0.75rem', textTransform: 'none',
    letterSpacing: '-0.01em', color: T.textDim,
    background: 'transparent', borderBottom: `1px solid ${T.border}`,
    paddingTop: 10, paddingBottom: 10,
    whiteSpace: 'nowrap',
  },
  '& .MuiTableBody-root .MuiTableRow-root': { background: `${T.surface} !important` },
  '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': { borderBottom: 'none' },
  '& .MuiTableBody-root .MuiTableRow-root:hover': {
    cursor: 'default',
  },
  '& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-root': {
    background: `${T.surfaceAlt} !important`, transition: 'background .15s',
  },
};

/* ═══════════════════════════════════════════════════════════════════════════
   Styles
   ═══════════════════════════════════════════════════════════════════════════ */
const useStyles = makeStyles((_theme: Theme) => ({

  root: {
    minHeight: '100vh', background: T.pageBg, display: 'flex', flexDirection: 'column',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: '0.8125rem',
    '& *': { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important" },
  },

  /* ── top bar ── */
  topBar: {
    background: '#061A38', height: 44,
    padding: '0 24px',
    display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
    borderBottom: '1px solid rgba(255,255,255,.06)',
  },
  backBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    color: 'rgba(255,255,255,.55)', fontSize: '0.75rem', fontWeight: 500,
    border: '1px solid rgba(255,255,255,.1)', background: 'transparent', cursor: 'pointer',
    padding: '3px 9px', borderRadius: 6,
    transition: 'all .15s', letterSpacing: '-0.01em',
    '&:hover': { color: '#fff', background: 'rgba(255,255,255,.1)', borderColor: 'rgba(255,255,255,.2)' },
  },
  crumbSep: { color: 'rgba(255,255,255,.2)', fontSize: '0.6875rem', userSelect: 'none' },
  crumbCurrent: { fontWeight: 600, fontSize: '0.75rem', color: 'rgba(255,255,255,.85)', letterSpacing: '-0.01em' },
  topBarRight: { marginLeft: 'auto' },
  refreshBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    border: '1px solid rgba(255,255,255,.15)', background: 'rgba(255,255,255,.06)',
    color: 'rgba(255,255,255,.6)', fontSize: '0.6875rem', fontWeight: 600,
    padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
    transition: 'all .15s', letterSpacing: '-0.01em',
    '&:hover': { background: 'rgba(255,255,255,.12)', color: '#fff', borderColor: 'rgba(255,255,255,.25)' },
  },

  /* ── hero ── */
  hero: {
    background: `linear-gradient(160deg, ${T.heroA} 0%, ${T.heroB} 35%, ${T.heroC} 70%, ${T.heroD} 100%)`,
    padding: '22px 24px 0',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""', position: 'absolute', inset: 0,
      background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.02\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
      pointerEvents: 'none',
    },
  },
  heroInner: { maxWidth: 1320, margin: '0 auto', position: 'relative', zIndex: 1 },
  heroTop: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 },
  heroTitle: { fontWeight: 700, fontSize: '1.25rem', color: '#ffffff', lineHeight: 1.15, letterSpacing: '-0.025em' },
  heroSub: { fontSize: '0.75rem', color: 'rgba(255,255,255,.5)', marginTop: 4, letterSpacing: '-0.01em' },
  heroTypePills: { display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' },
  heroTypePill: {
    display: 'flex', alignItems: 'center', gap: 4,
    background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.12)',
    borderRadius: 20, padding: '2px 9px',
    fontSize: '0.625rem', fontWeight: 600, color: 'rgba(255,255,255,.7)',
    letterSpacing: '-0.01em',
    transition: 'background .15s',
    '&:hover': { background: 'rgba(255,255,255,.14)' },
  },
  heroTypeDot: { width: 5, height: 5, borderRadius: '50%', flexShrink: 0 },
  heroTypeNew: {
    background: 'rgba(16,185,129,.25)', color: '#6ee7b7',
    fontSize: '0.5rem', fontWeight: 800, borderRadius: 10, padding: '1px 4px',
  },

  /* KPI strip — glass morphism inside hero */
  kpiStrip: {
    display: 'flex', gap: 0,
    background: 'rgba(255,255,255,.06)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255,255,255,.1)',
    borderBottom: 'none',
    borderRadius: '12px 12px 0 0',
    overflow: 'hidden',
    marginTop: 4,
  },
  kpiItem: {
    flex: '1 1 0',
    padding: '16px 18px',
    display: 'flex', alignItems: 'center', gap: 12,
    borderRight: '1px solid rgba(255,255,255,.08)',
    transition: 'background .15s',
    '&:last-child': { borderRight: 'none' },
    '&:hover': { background: 'rgba(255,255,255,.05)' },
  },
  kpiIconRing: {
    width: 38, height: 38, borderRadius: 10, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    position: 'relative',
  },
  kpiText: {},
  kpiVal: { fontWeight: 700, fontSize: '1.5rem', color: '#ffffff', lineHeight: 1, letterSpacing: '-0.03em' },
  kpiLabel: { fontSize: '0.5rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,.4)', marginTop: 3 },
  kpiDelta: {
    display: 'inline-flex', alignItems: 'center', gap: 1,
    marginTop: 4, fontSize: '0.5625rem', fontWeight: 700,
    color: '#6ee7b7', background: 'rgba(16,185,129,.15)',
    borderRadius: 10, padding: '1px 5px',
  },

  /* ── body ── */
  body: {
    flex: 1, maxWidth: 1320, width: '100%', margin: '0 auto',
    padding: '16px 24px 32px', boxSizing: 'border-box',
  },

  /* error */
  errBox: {
    background: 'rgba(244,63,94,.06)', border: '1px solid rgba(244,63,94,.2)',
    borderRadius: 8, padding: '8px 14px', marginBottom: 12,
    color: T.rose, fontSize: '0.75rem', letterSpacing: '-0.01em',
  },

  /* ── filter bar ── */
  filterBar: {
    background: T.surface, border: `1px solid ${T.border}`,
    borderRadius: 10, padding: '8px 14px',
    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
    marginBottom: 12, boxShadow: T.shadow,
  },
  filterLabel: {
    display: 'flex', alignItems: 'center', gap: 4,
    fontSize: '0.5625rem', fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase',
    color: T.textDim,
  },
  presetRow: { display: 'flex', gap: 2 },
  preset: {
    padding: '3px 9px', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'none',
    border: `1px solid ${T.border}`, borderRadius: 5, color: T.textSec,
    background: 'transparent', cursor: 'pointer', lineHeight: 1.6, minHeight: 0,
    letterSpacing: '-0.01em',
    transition: 'all .15s',
    '&:hover': { borderColor: T.accent, color: T.accent, background: T.indigoSoft },
  },
  presetOn: { background: `${T.accent} !important`, borderColor: `${T.accent} !important`, color: '#fff !important' },
  vDivider: { width: 1, height: 20, background: T.border, flexShrink: 0 },
  dateInputs: { display: 'flex', alignItems: 'center', gap: 6 },
  dateField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 6, fontSize: '0.75rem', color: T.text, background: T.surface,
      '& input': { padding: '5px 10px', letterSpacing: '-0.01em' },
      '& fieldset': { borderColor: T.border },
      '&:hover fieldset': { borderColor: T.accent },
      '&.Mui-focused fieldset': { borderColor: T.accent, borderWidth: 1.5 },
    },
    '& .MuiInputLabel-root': { fontSize: '0.6875rem', color: T.textSec },
    '& .MuiInputLabel-shrink': { fontSize: '0.75rem' },
  },
  dateSep: { color: T.textDim, fontSize: '0.75rem' },
  rangePill: {
    marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4,
    fontSize: '0.6875rem', fontWeight: 600, color: T.accent, letterSpacing: '-0.01em',
    background: T.indigoSoft, border: '1px solid rgba(79,70,229,.15)',
    borderRadius: 20, padding: '3px 10px',
  },

  /* ── section card ── */
  sec: { marginBottom: 10 },
  secCard: {
    background: T.surface, border: `1px solid ${T.border}`,
    borderRadius: 10, overflow: 'hidden', boxShadow: T.shadow,
  },
  secHead: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 16px', cursor: 'pointer', userSelect: 'none',
    transition: 'background .15s',
    borderBottom: '1px solid transparent',
  },
  secHeadOpen: { borderBottom: `1px solid ${T.border} !important` },
  secBar: { width: 3, height: 16, borderRadius: 2, flexShrink: 0 },
  secTitle: { fontWeight: 600, fontSize: '0.8125rem', color: T.text, flex: 1, letterSpacing: '-0.015em' },
  secBadge: {
    fontSize: '0.5625rem', fontWeight: 700,
    borderRadius: 20, padding: '2px 8px',
    border: `1px solid ${T.border}`, color: T.textSec, background: T.surfaceAlt,
    letterSpacing: '-0.01em',
  },
  secChev: { color: T.textDim, fontSize: '0.875rem !important' },
  secBody: { padding: '14px 16px 18px' },
  secBodyFlush: { padding: 0 },
  emptyState: {
    textAlign: 'center', padding: '32px 0',
    color: T.textDim, fontSize: '0.8125rem',
    border: `1.5px dashed ${T.borderMid}`, borderRadius: 10,
  },
  moreBtn: {
    marginTop: 12, textTransform: 'none', fontWeight: 600, fontSize: '0.6875rem',
    borderRadius: 7, color: T.accent, borderColor: 'rgba(79,70,229,.25)',
    padding: '4px 14px', letterSpacing: '-0.01em',
    '&:hover': { borderColor: T.accent, background: T.indigoSoft },
  },

  /* ── contributor cards ── */
  contribCard: {
    background: T.surface, border: `1px solid ${T.border}`,
    borderRadius: 10, padding: '14px 12px',
    height: '100%', boxSizing: 'border-box',
    display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
    transition: 'box-shadow .2s, border-color .2s, transform .2s',
    '&:hover': { boxShadow: T.shadowMd, borderColor: T.borderMid, transform: 'translateY(-2px)' },
  },
  contribAvatarWrap: { position: 'relative', display: 'inline-block', marginBottom: 8 },
  contribAvatar: { width: 38, height: 38, fontSize: '0.8125rem', fontWeight: 700, boxShadow: '0 2px 8px rgba(0,0,0,.12)' },
  rankBadge: {
    position: 'absolute' as const, top: -4, right: -4,
    width: 16, height: 16, borderRadius: '50%',
    background: T.surface, border: '2px solid',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.4375rem', fontWeight: 800,
  },
  contribName: { fontWeight: 600, fontSize: '0.75rem', color: T.text, marginBottom: 6, lineHeight: 1.3, wordBreak: 'break-word', letterSpacing: '-0.015em' },
  contribStats: { display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 8 },
  contribStat: { display: 'flex', alignItems: 'center', gap: 2, fontSize: '0.625rem', fontWeight: 600, borderRadius: 5, padding: '1px 6px' },
  contribTotal: { marginTop: 'auto', fontWeight: 700, fontSize: '1.125rem', color: T.text, lineHeight: 1, letterSpacing: '-0.03em' },
  contribTotalLbl: { fontSize: '0.5rem', color: T.textDim, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 2 },

  /* ── tables ── */
  tbl: TBL,
  rankCell: { width: 36, textAlign: 'center', color: T.textSec, fontWeight: 600 },
  dlBarRoot: { height: 4, borderRadius: 3, background: 'rgba(0,0,0,.06)' },
  dlBarFill: { borderRadius: 3, background: `linear-gradient(90deg, ${T.violet}, ${T.sky})` },

  /* action icons — no background, clean professional icon-only style */
  chipAdded: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 22, height: 22, flexShrink: 0,
    color: '#16a34a',
  },
  chipEdited: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 22, height: 22, flexShrink: 0,
    color: '#2563eb',
  },
  chipMaint: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 22, height: 22, flexShrink: 0,
    color: '#94a3b8',
  },
  typeChip: {
    fontWeight: 600, fontSize: '0.625rem', height: 20, borderRadius: 6,
    letterSpacing: '-0.01em',
  },

  /* charts */
  chartsRow: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 10 },
  chartCard: {
    background: T.surface, border: `1px solid ${T.border}`,
    borderRadius: 10, overflow: 'hidden', boxShadow: T.shadow,
    padding: '16px 18px 10px',
  },
  chartTitle: { fontWeight: 600, fontSize: '0.8125rem', color: T.text, letterSpacing: '-0.015em', marginBottom: 14 },
  chartSub:   { fontSize: '0.6875rem', color: T.textDim, marginTop: 2 },

  /* footer */
  footer: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
    padding: '14px 0', marginTop: 6, borderTop: `1px solid ${T.border}`,
    color: T.textDim, fontSize: '0.625rem', letterSpacing: '-0.01em',
  },
}));

/* ═══════════════════════════════════════════════════════════════════════════
   Section component
   ═══════════════════════════════════════════════════════════════════════════ */
interface SecProps {
  id: keyof typeof SEC;
  title: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  flush?: boolean;
}
function Section({ id, title, count, open, onToggle, children, flush }: SecProps) {
  const c = useStyles();
  const s = SEC[id];
  return (
    <Box className={c.sec}>
      <Box className={c.secCard}>
        <Box
          className={`${c.secHead} ${open ? c.secHeadOpen : ''}`}
          onClick={onToggle} role="button" aria-expanded={open}
          style={{ background: open ? s.headerBg : undefined }}
        >
          <Box className={c.secBar} style={{ background: s.bar }} />
          <Typography className={c.secTitle}>{title}</Typography>
          <span className={c.secBadge}>{count}</span>
          {open ? <ExpandLessIcon className={c.secChev} /> : <ExpandMoreIcon className={c.secChev} />}
        </Box>
        <Collapse in={open} timeout={200}>
          <Box className={flush ? c.secBodyFlush : c.secBody}>{children}</Box>
        </Collapse>
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Hook – unique marketplace visitors (from marketplace-backend)
   ═══════════════════════════════════════════════════════════════════════════ */
interface VisitorStats {
  uniqueUsers: number;
  totalVisits: number;
  fromDate:    string;
  toDate:      string;
  byDay: Array<{ day: string; unique_users: number; visits: number }>;
}

function useMarketplaceVisitors(from: string, to: string): {
  data: VisitorStats | null;
  loading: boolean;
} {
  const discovery = useApi(discoveryApiRef);
  const fetchApi  = useMarketplaceFetchApi();
  const [data,    setData]    = useState<VisitorStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const baseUrl = await discovery.getBaseUrl('marketplace');
        const params  = new URLSearchParams();
        if (from) params.set('from', from);
        if (to)   params.set('to',   to);
        const resp = await fetchApi.fetch(
          `${baseUrl}/visitors/stats?${params.toString()}`,
        );
        if (resp.ok) {
          const json = await resp.json();
          if (!cancelled) setData(json);
        }
      } catch {
        // non-critical — swallow silently
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [discovery, fetchApi, from, to]);

  return { data, loading };
}

/* ── Who Visited hook ── */
interface VisitorUserRecord {
  user_ref:   string;
  visits:     number;
  last_visit: string;
}

function useMarketplaceVisitorUsers(from: string, to: string): {
  users: VisitorUserRecord[];
  loading: boolean;
} {
  const discovery = useApi(discoveryApiRef);
  const fetchApi  = useMarketplaceFetchApi();
  const [users,   setUsers]   = useState<VisitorUserRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const baseUrl = await discovery.getBaseUrl('marketplace');
        const params  = new URLSearchParams({ from, to, limit: '200' });
        const resp    = await fetchApi.fetch(
          `${baseUrl}/visitors/users?${params.toString()}`,
        );
        if (resp.ok && !cancelled) {
          const json = await resp.json();
          setUsers(json.users ?? []);
        }
      } catch { /* non-critical */ } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [discovery, fetchApi, from, to]);

  return { users, loading };
}

/* ── Reactions stats hook ── */
interface ReactionEntry { user: string; type: string; at: string }
interface ArtifactReactions { artifact_type: string; upCount: number; downCount: number; reactions: ReactionEntry[] }

function useMarketplaceReactions(): {
  artifacts: Record<string, ArtifactReactions>;
  loading: boolean;
} {
  const discovery = useApi(discoveryApiRef);
  const fetchApi  = useMarketplaceFetchApi();
  const [artifacts, setArtifacts] = useState<Record<string, ArtifactReactions>>({});
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const baseUrl = await discovery.getBaseUrl('marketplace');
        const resp = await fetchApi.fetch(`${baseUrl}/reactions/stats`);
        if (resp.ok && !cancelled) {
          const json = await resp.json();
          setArtifacts(json.artifacts ?? {});
        }
      } catch { /* non-critical */ } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [discovery, fetchApi]);

  return { artifacts, loading };
}

/* ═══════════════════════════════════════════════════════════════════════════
   DashboardContent
   ═══════════════════════════════════════════════════════════════════════════ */
function DashboardContent() {
  const c = useStyles();
  const navigate = useNavigate();
  const { items, loading, error, refresh } = useMarketplace();
  const identityApi   = useApi(identityApiRef);
  const discoveryApi  = useApi(discoveryApiRef);
  const fetchApi      = useMarketplaceFetchApi();

  /* ── access guard ── */
  const [authChecked, setAuthChecked] = useState(false);
  const [isAllowed,   setIsAllowed]   = useState(false);

  /* ── downloads-by-type from database ── */
  const [dlByTypeDb, setDlByTypeDb] = useState<Record<string, number>>({});
  const [dlByTypeLoading, setDlByTypeLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setDlByTypeLoading(true);
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('marketplace');
        const resp = await fetchApi.fetch(`${baseUrl}/downloads/stats?groupBy=type`);
        if (resp.ok) {
          const json = await resp.json() as { stats: Record<string, number> };
          if (!cancelled) setDlByTypeDb(json.stats ?? {});
        }
      } catch {
        // non-critical — chart will fall back to empty
      } finally {
        if (!cancelled) setDlByTypeLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [discoveryApi, fetchApi]);

  /* ── downloads-by-artifact from database (pure DB, no seed values) ── */
  const [dlByArtifactDb, setDlByArtifactDb] = useState<Record<string, number>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('marketplace');
        const resp = await fetchApi.fetch(`${baseUrl}/downloads/stats`);
        if (resp.ok) {
          const json = await resp.json() as { stats: Record<string, number> };
          if (!cancelled) setDlByArtifactDb(json.stats ?? {});
        }
      } catch {
        // non-critical
      }
    })();
    return () => { cancelled = true; };
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    identityApi.getProfileInfo().then(profile => {
      const email = (profile.email ?? '').toLowerCase();
      setIsAllowed(MARKETPLACE_ADMIN_EMAILS.has(email));
      setAuthChecked(true);
    }).catch(() => {
      setIsAllowed(false);
      setAuthChecked(true);
    });
  }, [identityApi]);

  useEffect(() => {
    if (authChecked && !isAllowed) {
      navigate('/ai-marketplace', { replace: true });
    }
  }, [authChecked, isAllowed, navigate]);

  const [startDate, setStartDate] = useState(() => ago(30));
  const [endDate,   setEndDate]   = useState(today);
  const [preset,    setPreset]    = useState<number | null>(30);
  const [moreNew,   setMoreNew]   = useState(false);
  const [moreUpd,   setMoreUpd]   = useState(false);
  const [exp, setExp] = useState({ newItems: true, updated: true, contribs: true, downloads: true, log: true, visitors: true, reactions: true });
  const [whoOpen, setWhoOpen] = useState(false); // Who Visited sub-panel — collapsed by default
  const flip = useCallback((k: keyof typeof exp) => setExp(p => ({ ...p, [k]: !p[k] })), []);

  /* ── unique visitors from marketplace-backend ── */
  const { data: visitorsData, loading: visitorsLoading } = useMarketplaceVisitors(startDate, endDate);
  const { users: visitorUsers, loading: visitorUsersLoading } = useMarketplaceVisitorUsers(startDate, endDate);
  const { artifacts: reactionsData, loading: reactionsLoading } = useMarketplaceReactions();
  const [reactionsFilter, setReactionsFilter] = useState('all');

  useEffect(() => {
    document.title = 'Marketplace Analytics | Verint Dev Portal';
    return () => { document.title = 'AI Marketplace | Verint Dev Portal'; };
  }, []);

  const pick = useCallback((d: number | null) => {
    setPreset(d); setMoreNew(false); setMoreUpd(false);
    setEndDate(today()); setStartDate(d === null ? '' : ago(d));
  }, []);
  const onS = (v: string) => { setStartDate(v); setPreset(null); };
  const onE = (v: string) => { setEndDate(v);   setPreset(null); };

  const sMs     = useMemo(() => startDate ? ms(startDate) : 0, [startDate]);
  const eMs     = useMemo(() => endDate ? msEnd(endDate) : Date.now() + 86_400_000, [endDate]);
  const inRange = useCallback((d?: string) => { if (!d) return false; const t = ms(d); return t >= sMs && t <= eMs; }, [sMs, eMs]);

  const newItems = useMemo(() => sortByField(items.filter(i => inRange(i.createdAt)), 'createdAt'), [items, inRange]);
  const updItems = useMemo(() => {
    if (!startDate) {
      // "All" mode: every item was created during the full range, so the new/updated
      // split by creation date is meaningless. Instead show items that have ever been
      // edited after their initial creation (updatedAt exists and differs from createdAt).
      return sortByField(
        items.filter(i => i.updatedAt && i.createdAt && i.updatedAt !== i.createdAt),
        'updatedAt',
      );
    }
    const ids = new Set(newItems.map(i => i.id));
    return sortByField(items.filter(i => !ids.has(i.id) && inRange(i.updatedAt)), 'updatedAt');
  }, [items, newItems, inRange, startDate]);

  const typeStats = useMemo(() => MARKETPLACE_TYPES.map(t => {
    const all = items.filter(i => i.type === t);
    return { type: t, total: all.length, newC: all.filter(i => inRange(i.createdAt)).length };
  }), [items, inRange]);

  // totalDl and topDl use only real DB download records (no manifest seed values)
  const totalDl = useMemo(() => Object.values(dlByArtifactDb).reduce((s, v) => s + v, 0), [dlByArtifactDb]);
  const topDl   = useMemo(() => {
    const itemById = new Map(items.map(i => [i.id, i]));
    const l = Object.entries(dlByArtifactDb)
      .filter(([, count]) => count > 0)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10);
    const mx = l[0]?.[1] ?? 1;
    return l.map(([id, count]) => {
      const item = itemById.get(id);
      return { name: item?.displayName || item?.name || id, type: (item?.type ?? 'agent') as ItemType, count, pct: (count / mx) * 100 };
    });
  }, [dlByArtifactDb, items]);

  const contribs = useMemo<Contrib[]>(() => {
    const r: Contrib[] = [];
    for (const it of items) {
      const tl = TYPE_META[it.type]?.label ?? it.type;
      const au = it.author ? cleanName(it.author) : 'Unknown';
      const nm = it.displayName || it.name;
      if (it.createdAt && inRange(it.createdAt))
        r.push({ action: 'Added', type: tl, item: nm, author: au, date: it.createdAt, ts: ms(it.createdAt) });
      if (it.status === 'maintenance' && it.updatedAt && inRange(it.updatedAt)) {
        r.push({ action: 'Maintenance', type: tl, item: nm, author: au, date: it.updatedAt, ts: ms(it.updatedAt) });
        continue;
      }
      if (it.updatedAt && it.createdAt && it.updatedAt !== it.createdAt && it.status !== 'maintenance' && inRange(it.updatedAt))
        r.push({ action: 'Edited', type: tl, item: nm, author: au, date: it.updatedAt, ts: ms(it.updatedAt) });
    }
    return r.sort((a, b) => b.ts - a.ts);
  }, [items, inRange]);

  const people = useMemo<Person[]>(() => {
    const m = new Map<string, Person>();
    for (const r of contribs) {
      if (!m.has(r.author)) m.set(r.author, { name: r.author, added: 0, edited: 0, maint: 0, total: 0, rank: 0 });
      const p = m.get(r.author)!;
      if (r.action === 'Added') p.added++; else if (r.action === 'Edited') p.edited++; else p.maint++;
      p.total++;
    }
    const arr = Array.from(m.values()).sort((a, b) => b.total - a.total);
    arr.forEach((p, i) => { p.rank = i + 1; });
    return arr;
  }, [contribs]);

  const periodStr = useMemo(() => {
    if (!startDate && !endDate) return 'All time';
    if (!startDate) return `Up to ${fmt(endDate)}`;
    if (!endDate) return `From ${fmt(startDate)}`;
    return `${fmt(startDate)} – ${fmt(endDate)}`;
  }, [startDate, endDate]);

  const shownNew = moreNew ? newItems : newItems.slice(0, MAX_CARDS);
  const shownUpd = moreUpd ? updItems : updItems.slice(0, MAX_CARDS);
  const chipCls  = (a: string) => a === 'Added' ? c.chipAdded : a === 'Edited' ? c.chipEdited : c.chipMaint;

  /* ── chart data ── */
  const donutData = useMemo(() =>
    typeStats
      .filter(t => t.total > 0)
      .map(t => ({ name: TYPE_META[t.type]?.label ?? t.type, value: t.total, color: TYPE_META[t.type]?.color ?? T.accent }))
  , [typeStats]);

  const dlByType = useMemo(() =>
    Object.entries(dlByTypeDb)
      .map(([type, count]) => ({
        name: TYPE_META[type as ItemType]?.label ?? type,
        downloads: count,
        color: TYPE_META[type as ItemType]?.color ?? T.accent,
      }))
      .filter(t => t.downloads > 0)
      .sort((a, b) => b.downloads - a.downloads)
  , [dlByTypeDb]);

  const reactionsChartData = useMemo(() => {
    const entries = Object.entries(reactionsData);
    const filtered = reactionsFilter === 'all' ? entries : entries.filter(([, info]) => info.artifact_type === reactionsFilter);
    return filtered
      .map(([name, info]) => ({
        name: cleanName(name),
        likes: info.upCount,
        dislikes: info.downCount,
        total: info.upCount + info.downCount,
        // colour each row by its artifact type; DB retains all entries regardless
        typeColor: TYPE_META[info.artifact_type as ItemType]?.color ?? '#94a3b8',
      }))
      .filter(d => d.total > 0)
      .sort((a, b) => b.total - a.total)
      .slice(0, REACTIONS_CHART_LIMIT);
  }, [reactionsData, reactionsFilter]);

  const activityData = useMemo(() => {
    const days = 14;
    const dayMap: Record<string, { date: string; Added: number; Edited: number; Maintenance: number }> = {};
    for (let i = days - 1; i >= 0; i--) {
      const d = isoDate(new Date(Date.now() - i * 86_400_000));
      dayMap[d] = { date: d, Added: 0, Edited: 0, Maintenance: 0 };
    }
    for (const r of contribs) {
      const d = r.date.slice(0, 10);
      if (dayMap[d]) dayMap[d][r.action]++;
    }
    return Object.values(dayMap).map(row => ({
      ...row,
      date: new Date(row.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    }));
  }, [contribs]);

  const kpis = [
    { label: 'Total Items',     val: items.length,   delta: null },
    { label: 'New Items',       val: newItems.length, delta: newItems.length  > 0 ? `+${newItems.length}`  : null },
    { label: 'Updated',         val: updItems.length, delta: updItems.length  > 0 ? `+${updItems.length}`  : null },
    { label: 'Contributors',    val: people.length,   delta: null },
    { label: 'Downloads',       val: totalDl,          delta: null },
    { label: 'Unique Visitors', val: visitorsLoading ? '…' : (visitorsData?.uniqueUsers ?? '—'), delta: null },
  ];

  /* ── auth gate: show spinner until identity resolves ── */
  if (!authChecked || !isAllowed) {
    return (
      <Box style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.pageBg }}>
        <CircularProgress size={28} style={{ color: T.accent }} />
      </Box>
    );
  }

  return (
    <Box className={c.root}>

      {/* ── Top bar (dark, branded) ──────────────────────────────────────── */}
      <Box className={c.topBar}>
        <button className={c.backBtn} onClick={() => navigate('/ai-marketplace')}>
          <ArrowBackIcon style={{ fontSize: '0.875rem' }} /> AI Marketplace
        </button>
        <span className={c.crumbSep}>/</span>
        <Typography className={c.crumbCurrent}>Analytics</Typography>
        <Box className={c.topBarRight}>
          <Tooltip title="Refresh data">
            <button className={c.refreshBtn} onClick={refresh}>
              <RefreshIcon style={{ fontSize: '0.8125rem' }} /> Refresh
            </button>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Hero (gradient with KPI strip) ──────────────────────────────── */}
      <Box className={c.hero}>
        <Box className={c.heroInner}>

          {/* Hero header row */}
          <Box className={c.heroTop}>
            <Box>
              <Typography className={c.heroTitle}>Marketplace Analytics</Typography>
              <Typography className={c.heroSub}>{periodStr} · {items.length} items across {typeStats.filter(t => t.total > 0).length} categories</Typography>
            </Box>
            {/* Type breakdown pills */}
            <Box className={c.heroTypePills}>
              {typeStats.filter(t => t.total > 0).map(({ type, total, newC }) => {
                const meta = TYPE_META[type];
                return (
                  <Box key={type} className={c.heroTypePill}>
                    <Box className={c.heroTypeDot} style={{ background: meta?.color ?? '#fff' }} />
                    <span style={{ color: 'rgba(255,255,255,.45)' }}>{meta?.label ?? type}</span>
                    <span style={{ color: '#fff', fontWeight: 700 }}>{loading ? '—' : total}</span>
                    {!loading && newC > 0 && <span className={c.heroTypeNew}>+{newC}</span>}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* KPI strip — glass morphism bottom of hero */}
          <Box className={c.kpiStrip}>
            {kpis.map(({ label, val, delta }, i) => {
              const def = KPI_DEF[i];
              const Icon = def.icon;
              return (
                <Box key={label} className={c.kpiItem}>
                  <Box className={c.kpiIconRing} style={{
                    background: def.grad,
                    boxShadow: `0 4px 12px ${def.glow}`,
                  }}>
                    <Icon style={{ color: '#fff', fontSize: '1rem' }} />
                  </Box>
                  <Box className={c.kpiText}>
                    <Typography className={c.kpiVal}>{loading ? '—' : val}</Typography>
                    <Typography className={c.kpiLabel}>{label}</Typography>
                    {!loading && delta && (
                      <Box className={c.kpiDelta}>
                        <ArrowDropUpIcon style={{ fontSize: '0.875rem', marginRight: -2 }} />
                        {delta} this period
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <Box className={c.body}>

        {error && <Box className={c.errBox}>{error}</Box>}

        {/* ── Period filter bar ── */}
        <Box className={c.filterBar}>
          <Typography className={c.filterLabel} component="span">
            <DateRangeIcon style={{ fontSize: '0.8125rem' }} /> Period
          </Typography>
          <Box className={c.presetRow}>
            {PRESETS.map(({ label, days }) => (
              <Button key={label} size="small"
                className={`${c.preset} ${preset === days ? c.presetOn : ''}`}
                onClick={() => pick(days)}>
                {label}
              </Button>
            ))}
          </Box>
          <Box className={c.vDivider} />
          <Box className={c.dateInputs}>
            <TextField label="From" type="date" variant="outlined" size="small"
              value={startDate} onChange={e => onS(e.target.value)}
              className={c.dateField} InputLabelProps={{ shrink: true }}
              inputProps={{ max: endDate || today() }} style={{ width: 135 }} />
            <Typography className={c.dateSep}>→</Typography>
            <TextField label="To" type="date" variant="outlined" size="small"
              value={endDate} onChange={e => onE(e.target.value)}
              className={c.dateField} InputLabelProps={{ shrink: true }}
              inputProps={{ min: startDate, max: today() }} style={{ width: 135 }} />
          </Box>
          <Box className={c.rangePill}>
            <DateRangeIcon style={{ fontSize: '0.75rem' }} /> {periodStr}
          </Box>
        </Box>

        {/* ── Charts Row ── */}
        {!loading && (
          <Box className={c.chartsRow}>

            {/* Donut — Items by Type */}
            <Box className={c.chartCard}>
              <Typography className={c.chartTitle}>Catalog Breakdown</Typography>
              <Typography className={c.chartSub} style={{ marginTop: -10, marginBottom: 12 }}>Items by type</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={donutData} dataKey="value" cx="50%" cy="50%" innerRadius={52} outerRadius={80} paddingAngle={2}>
                    {donutData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <RTooltip
                    formatter={(value: any, name: any) => [value, name]}
                    contentStyle={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: '0.75rem', fontFamily: 'Inter, sans-serif' }}
                  />
                  <Legend iconType="circle" iconSize={8}
                    formatter={(value: string) => <span style={{ fontSize: '0.6875rem', color: T.textSec }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>

            {/* Stacked Bar — Activity timeline (last 14 days) */}
            <Box className={c.chartCard}>
              <Typography className={c.chartTitle}>Activity Timeline</Typography>
              <Typography className={c.chartSub} style={{ marginTop: -10, marginBottom: 12 }}>Last 14 days · contributions</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={activityData} margin={{ top: 0, right: 0, left: -28, bottom: 0 }} barSize={14}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: '0.5625rem', fill: T.textDim }} tickLine={false} axisLine={false} interval={1} />
                  <YAxis tick={{ fontSize: '0.5625rem', fill: T.textDim }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <RTooltip
                    contentStyle={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: '0.75rem', fontFamily: 'Inter, sans-serif' }}
                  />
                  <Bar dataKey="Added" stackId="a" fill={T.emerald} radius={[0,0,0,0]} />
                  <Bar dataKey="Edited" stackId="a" fill={T.sky} radius={[0,0,0,0]} />
                  <Bar dataKey="Maintenance" stackId="a" fill={T.violet} radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>

            {/* Horizontal Bar — Downloads by Type */}
            <Box className={c.chartCard}>
              <Typography className={c.chartTitle}>Downloads by Type</Typography>
              <Typography className={c.chartSub} style={{ marginTop: -10, marginBottom: 12 }}>All time · from database</Typography>
              {dlByTypeLoading
                ? <Box style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={20} style={{ color: T.accent }} /></Box>
                : dlByType.length === 0
                  ? <Box className={c.emptyState} style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No download data</Box>
                  : (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={dlByType} layout="vertical" margin={{ top: 0, right: 28, left: 0, bottom: 0 }} barSize={14}>
                        <CartesianGrid strokeDasharray="3 3" stroke={T.border} horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: '0.5625rem', fill: T.textDim }} tickLine={false} axisLine={false} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: '0.625rem', fill: T.textSec }} tickLine={false} axisLine={false} width={70} />
                        <RTooltip
                          contentStyle={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: '0.75rem', fontFamily: 'Inter, sans-serif' }}
                        />
                        <Bar dataKey="downloads" radius={[0,4,4,0]}>
                          {dlByType.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                          <LabelList dataKey="downloads" position="right" style={{ fontSize: '0.625rem', fill: T.textSec }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )
              }
            </Box>

            {/* Horizontal Stacked Bar — Reactions by Artifact */}
            <Box className={c.chartCard}>
              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography className={c.chartTitle}>Reactions by Artifact</Typography>
                {Object.keys(reactionsData).length > 0 && (
                  <TextField
                    select
                    size="small"
                    value={reactionsFilter}
                    onChange={e => setReactionsFilter(e.target.value)}
                    variant="outlined"
                    style={{ minWidth: 140 }}
                    InputProps={{ style: { fontSize: '0.75rem', height: 28 } }}
                  >
                    <MenuItem value="all" style={{ fontSize: '0.75rem' }}>All Artifacts</MenuItem>
                    {Array.from(new Set(Object.values(reactionsData).map(v => v.artifact_type))).sort().map(type => (
                      <MenuItem key={type} value={type} style={{ fontSize: '0.75rem' }}>
                        {TYPE_META[type as ItemType]?.label ?? cleanName(type)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Box>
              <Typography className={c.chartSub} style={{ marginTop: -10, marginBottom: 12 }}>Top reacted items · max: {REACTIONS_CHART_LIMIT}</Typography>
              {reactionsLoading
                ? <Box style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={20} style={{ color: T.accent }} /></Box>
                : reactionsChartData.length === 0
                  ? <Box className={c.emptyState} style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No reactions data</Box>
                  : (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={reactionsChartData} layout="vertical" margin={{ top: 0, right: 28, left: -6, bottom: 0 }} barSize={14}>
                        <CartesianGrid strokeDasharray="3 3" stroke={T.border} horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: '0.5625rem', fill: T.textDim }} tickLine={false} axisLine={false} allowDecimals={false} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: '0.625rem', fill: T.textSec }} tickLine={false} axisLine={false} width={110} />
                        <RTooltip
                          contentStyle={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: '0.75rem', fontFamily: 'Inter, sans-serif' }}
                        />
                        <Bar dataKey="likes" stackId="r" name="Likes" radius={[0,4,4,0]} fill='#1565C0' />
                        <Bar dataKey="dislikes" stackId="r" fill='#C62828' radius={[0,4,4,0]} name="Dislikes" />
                      </BarChart>
                    </ResponsiveContainer>
                  )
              }
            </Box>
          </Box>
        )}

        {loading ? (
          <Box style={{ textAlign: 'center', padding: '60px 0' }}>
            <CircularProgress size={28} style={{ color: T.accent }} />
            <Typography style={{ marginTop: 10, color: T.textSec, fontSize: '0.8125rem' }}>Loading analytics…</Typography>
          </Box>
        ) : (
          <>

            {/* ── New Items ── */}
            <Section id="newItems" title="New Items" count={newItems.length} open={exp.newItems} onToggle={() => flip('newItems')}>
              {newItems.length === 0
                ? <Box className={c.emptyState}>No new items added in this period.</Box>
                : (
                  <>
                    <Grid container spacing={2}>
                      {shownNew.map(it => <Grid item xs={12} sm={6} md={4} xl={3} key={it.id}><MarketplaceCard item={it} /></Grid>)}
                    </Grid>
                    {newItems.length > MAX_CARDS && (
                      <Button variant="outlined" className={c.moreBtn} onClick={() => setMoreNew(v => !v)}>
                        {moreNew ? 'Show fewer' : `Show all ${newItems.length}`}
                      </Button>
                    )}
                  </>
                )}
            </Section>

            {/* ── Updated Items ── */}
            <Section id="updated" title="Updated Items" count={updItems.length} open={exp.updated} onToggle={() => flip('updated')}>
              {updItems.length === 0
                ? <Box className={c.emptyState}>No items updated in this period.</Box>
                : (
                  <>
                    <Grid container spacing={2}>
                      {shownUpd.map(it => <Grid item xs={12} sm={6} md={4} xl={3} key={it.id}><MarketplaceCard item={it} /></Grid>)}
                    </Grid>
                    {updItems.length > MAX_CARDS && (
                      <Button variant="outlined" className={c.moreBtn} onClick={() => setMoreUpd(v => !v)}>
                        {moreUpd ? 'Show fewer' : `Show all ${updItems.length}`}
                      </Button>
                    )}
                  </>
                )}
            </Section>

            {/* ── Active Contributors ── */}
            <Section id="contribs" title="Active Contributors" count={people.length} open={exp.contribs} onToggle={() => flip('contribs')}>
              {people.length === 0
                ? <Box className={c.emptyState}>No contributor activity in this period.</Box>
                : (
                  <Grid container spacing={2}>
                    {people.map(p => (
                      <Grid item xs={6} sm={4} md={3} lg={2} key={p.name}>
                        <Paper className={c.contribCard} elevation={0}>
                          <Box className={c.contribAvatarWrap}>
                            <Avatar className={c.contribAvatar} style={{ background: pickColor(p.name) }}>
                              {initials(p.name)}
                            </Avatar>
                            {p.rank <= 3 && (
                              <Box className={c.rankBadge} style={{
                                borderColor: p.rank === 1 ? '#f59e0b' : p.rank === 2 ? '#94a3b8' : '#cd7c32',
                                color:       p.rank === 1 ? '#d97706' : p.rank === 2 ? '#64748b' : '#b45309',
                              }}>
                                {p.rank}
                              </Box>
                            )}
                          </Box>
                          <Typography className={c.contribName}>{p.name}</Typography>
                          <Box className={c.contribStats}>
                            {p.added  > 0 && <Box className={c.contribStat} style={{ background: T.emeraldSoft, color: T.emeraldText }}><AddIcon style={{ fontSize: '0.6875rem' }} />{p.added}</Box>}
                            {p.edited > 0 && <Box className={c.contribStat} style={{ background: T.orangeSoft,  color: T.orangeText  }}><EditIcon style={{ fontSize: '0.6875rem' }} />{p.edited}</Box>}
                            {p.maint  > 0 && <Box className={c.contribStat} style={{ background: T.skySoft,     color: T.skyText     }}><BuildIcon style={{ fontSize: '0.6875rem' }} />{p.maint}</Box>}
                          </Box>
                          <Typography className={c.contribTotal}>{p.total}</Typography>
                          <Typography className={c.contribTotalLbl}>contributions</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
            </Section>

            {/* ── Top Downloads ── */}
            {topDl.length > 0 && (
              <Section id="downloads" title="Top Downloads" count={totalDl} open={exp.downloads} onToggle={() => flip('downloads')} flush>
                <Table className={c.tbl}>
                  <TableHead>
                    <TableRow>
                      <TableCell className={c.rankCell} style={{ paddingLeft: 24 }}>#</TableCell>
                      <TableCell>Item ↑↓</TableCell>
                      <TableCell style={{ width: 110 }}>Type</TableCell>
                      <TableCell style={{ width: 200 }}>Volume</TableCell>
                      <TableCell align="right" style={{ width: 80, paddingRight: 24 }}>Downloads ↑↓</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topDl.map(({ name, type, count, pct }, i) => (
                      <TableRow key={name}>
                        <TableCell className={c.rankCell} style={{ paddingLeft: 24 }}>
                          {i < 3
                            ? <EmojiEventsIcon style={{ fontSize: '1rem', color: i === 0 ? '#f59e0b' : i === 1 ? '#94a3b8' : '#cd7c32', verticalAlign: 'middle' }} />
                            : <span style={{ color: T.textDim }}>{i + 1}</span>}
                        </TableCell>
                        <TableCell><span style={{ fontWeight: 600, color: T.text }}>{name}</span></TableCell>
                        <TableCell>
                          {(() => {
                            const meta = TYPE_META[type];
                            const col = meta?.color ?? T.accent;
                            const bg = `${col}18`;
                            return (
                              <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: bg, borderRadius: 10, padding: '2px 7px 2px 2px' }}>
                                <Box style={{ width: 16, height: 16, borderRadius: '50%', background: meta?.gradient ?? col, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  {meta && <TypeIcon iconName={meta.iconName} style={{ color: '#fff', fontSize: '0.625rem' }} fontSize="inherit" />}
                                </Box>
                                <span style={{ fontSize: '0.625rem', fontWeight: 700, color: col, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>{meta?.label ?? type}</span>
                              </Box>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <LinearProgress variant="determinate" value={pct} classes={{ root: c.dlBarRoot, bar: c.dlBarFill }} />
                        </TableCell>
                        <TableCell align="right" style={{ paddingRight: 24 }}>
                          <span style={{ fontWeight: 700, fontSize: '0.9375rem', color: T.text }}>{count}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Box style={{ padding: '10px 24px', borderTop: `1px solid ${T.border}` }}>
                  <Typography style={{ fontSize: '0.75rem', color: T.textDim }}>Showing top {topDl.length} by download count · All time</Typography>
                </Box>
              </Section>
            )}

            {/* ── Contribution Log ── */}
            <Section id="log" title="Contribution Log" count={contribs.length} open={exp.log} onToggle={() => flip('log')} flush>
              {contribs.length === 0
                ? <Box style={{ padding: '16px 18px 20px' }}><Box className={c.emptyState}>No contributions in this period.</Box></Box>
                : (
                  <Table className={c.tbl}>
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ width: 185, paddingLeft: 24 }}>Contributor</TableCell>
                        <TableCell>Item ↑↓</TableCell>
                        <TableCell style={{ width: 90 }}>Action</TableCell>
                        <TableCell style={{ width: 100 }}>Type</TableCell>
                        <TableCell style={{ width: 125, paddingRight: 24 }}>Date ↑↓</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contribs.map((r, i) => (
                        <TableRow key={`${r.item}-${r.action}-${i}`}>
                          <TableCell style={{ paddingLeft: 24 }}>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <Avatar style={{ width: 26, height: 26, fontSize: '0.5625rem', fontWeight: 700, background: pickColor(r.author), flexShrink: 0 }}>
                                {initials(r.author)}
                              </Avatar>
                              <span style={{ color: T.text, fontWeight: 500 }}>{r.author}</span>
                            </Box>
                          </TableCell>
                          <TableCell><span style={{ fontWeight: 600, color: T.text }}>{r.item}</span></TableCell>
                          <TableCell>
                            <Tooltip title={r.action} placement="top" arrow>
                              <Box className={chipCls(r.action)}>
                                {r.action === 'Added'
                                  ? <AddIcon style={{ fontSize: '0.875rem' }} />
                                  : r.action === 'Edited'
                                  ? <EditIcon style={{ fontSize: '0.875rem' }} />
                                  : <BuildIcon style={{ fontSize: '0.875rem' }} />}
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const meta = TYPE_META[r.type.toLowerCase() as ItemType];
                              const col = meta?.color ?? T.accent;
                              const bg = `${col}18`;
                              return (
                                <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: bg, borderRadius: 10, padding: '2px 7px 2px 2px' }}>
                                  <Box style={{ width: 16, height: 16, borderRadius: '50%', background: meta?.gradient ?? col, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    {meta && <TypeIcon iconName={meta.iconName} style={{ color: '#fff', fontSize: '0.625rem' }} fontSize="inherit" />}
                                  </Box>
                                  <span style={{ fontSize: '0.625rem', fontWeight: 700, color: col, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>{meta?.label ?? r.type}</span>
                                </Box>
                              );
                            })()}
                          </TableCell>
                          <TableCell style={{ paddingRight: 24 }}>
                            <span style={{ color: T.textSec }}>{fmtShort(r.date)}</span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              {contribs.length > 0 && (
                <Box style={{ padding: '10px 24px', borderTop: `1px solid ${T.border}` }}>
                  <Typography style={{ fontSize: '0.75rem', color: T.textDim }}>Last updated: {fmtShort(contribs[0]?.date ?? '')}</Typography>
                </Box>
              )}
            </Section>

            {/* ── Unique Visitors ── */}
            <Section id="visitors" title="Unique Visitors" count={visitorsLoading ? 0 : (visitorsData?.uniqueUsers ?? 0)} open={exp.visitors} onToggle={() => flip('visitors')}>
              {visitorsLoading ? (
                <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 0', gap: 8 }}>
                  <CircularProgress size={16} style={{ color: '#06b6d4' }} />
                  <Typography style={{ color: T.textDim, fontSize: '0.8125rem' }}>Loading visitor data…</Typography>
                </Box>
              ) : !visitorsData ? (
                <Box style={{
                  textAlign: 'center', padding: '28px 0', color: T.textDim,
                  border: `1.5px dashed ${T.borderMid}`, borderRadius: 10,
                }}>
                  <VisibilityIcon style={{ fontSize: '1.5rem', color: T.borderMid, marginBottom: 4 }} />
                  <Typography style={{ fontSize: '0.8125rem' }}>No visitor data available</Typography>
                </Box>
              ) : (() => {
                const avgVisits = visitorsData.uniqueUsers > 0 ? (visitorsData.totalVisits / visitorsData.uniqueUsers).toFixed(1) : '—';
                const maxVisitsInUsers = visitorUsers.length > 0 ? Math.max(...visitorUsers.map(u => u.visits)) : 1;
                const chartData = [...visitorsData.byDay].map(row => ({
                  day: new Date(row.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                  unique: Number(row.unique_users),
                  total: Number(row.visits),
                }));

                return (
                  <>
                    {/* ── Stat cards row ── */}
                    <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
                      {/* Unique Visitors */}
                      <Box style={{
                        background: 'linear-gradient(135deg, rgba(6,182,212,.08) 0%, rgba(8,145,178,.04) 100%)',
                        border: '1px solid rgba(6,182,212,.15)', borderRadius: 10, padding: '12px 14px',
                        display: 'flex', alignItems: 'center', gap: 10,
                      }}>
                        <Box style={{
                          width: 34, height: 34, borderRadius: 9,
                          background: 'linear-gradient(135deg,#0891b2,#06b6d4)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          boxShadow: '0 2px 8px rgba(6,182,212,.3)',
                        }}>
                          <PersonIcon style={{ color: '#fff', fontSize: '1rem' }} />
                        </Box>
                        <Box>
                          <Typography style={{ fontSize: '1.375rem', fontWeight: 700, color: '#0891b2', lineHeight: 1, letterSpacing: '-0.03em' }}>
                            {visitorsData.uniqueUsers}
                          </Typography>
                          <Typography style={{ fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: T.textDim, marginTop: 1 }}>
                            Unique Visitors
                          </Typography>
                        </Box>
                      </Box>
                      {/* Total Visits */}
                      <Box style={{
                        background: 'linear-gradient(135deg, rgba(79,70,229,.06) 0%, rgba(99,102,241,.03) 100%)',
                        border: '1px solid rgba(79,70,229,.12)', borderRadius: 10, padding: '12px 14px',
                        display: 'flex', alignItems: 'center', gap: 10,
                      }}>
                        <Box style={{
                          width: 34, height: 34, borderRadius: 9,
                          background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          boxShadow: '0 2px 8px rgba(79,70,229,.3)',
                        }}>
                          <VisibilityIcon style={{ color: '#fff', fontSize: '1rem' }} />
                        </Box>
                        <Box>
                          <Typography style={{ fontSize: '1.375rem', fontWeight: 700, color: '#4f46e5', lineHeight: 1, letterSpacing: '-0.03em' }}>
                            {visitorsData.totalVisits}
                          </Typography>
                          <Typography style={{ fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: T.textDim, marginTop: 1 }}>
                            Total Visits
                          </Typography>
                        </Box>
                      </Box>
                      {/* Avg Visits/Person */}
                      <Box style={{
                        background: 'linear-gradient(135deg, rgba(16,185,129,.06) 0%, rgba(5,150,105,.03) 100%)',
                        border: '1px solid rgba(16,185,129,.12)', borderRadius: 10, padding: '12px 14px',
                        display: 'flex', alignItems: 'center', gap: 10,
                      }}>
                        <Box style={{
                          width: 34, height: 34, borderRadius: 9,
                          background: 'linear-gradient(135deg,#059669,#10b981)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          boxShadow: '0 2px 8px rgba(16,185,129,.3)',
                        }}>
                          <AssessmentIcon style={{ color: '#fff', fontSize: '1rem' }} />
                        </Box>
                        <Box>
                          <Typography style={{ fontSize: '1.375rem', fontWeight: 700, color: '#059669', lineHeight: 1, letterSpacing: '-0.03em' }}>
                            {avgVisits}
                          </Typography>
                          <Typography style={{ fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: T.textDim, marginTop: 1 }}>
                            Avg / Person
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* ── Daily trend chart ── */}
                    {chartData.length > 0 && (
                      <Box style={{
                        background: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 10,
                        padding: '12px 14px 6px', marginBottom: 14,
                      }}>
                        <Typography style={{ fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: T.textDim, marginBottom: 8 }}>
                          Daily Trend
                        </Typography>
                        <ResponsiveContainer width="100%" height={110}>
                          <BarChart data={chartData} barCategoryGap="20%">
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,.06)" vertical={false} />
                            <XAxis dataKey="day" tick={{ fontSize: 10, fill: T.textDim }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 10, fill: T.textDim }} axisLine={false} tickLine={false} width={28} allowDecimals={false} />
                            <RTooltip
                              contentStyle={{
                                background: '#1e293b', border: 'none', borderRadius: 8, padding: '6px 10px',
                                fontSize: '0.6875rem', color: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,.25)',
                              }}
                              itemStyle={{ color: '#e2e8f0', fontSize: '0.6875rem' }}
                              labelStyle={{ color: '#94a3b8', fontSize: '0.625rem', fontWeight: 600, marginBottom: 2 }}
                              cursor={{ fill: 'rgba(6,182,212,.06)' }}
                            />
                            <Bar dataKey="total" name="Total Visits" fill="rgba(79,70,229,.2)" radius={[3, 3, 0, 0]} />
                            <Bar dataKey="unique" name="Unique Visitors" fill="#06b6d4" radius={[3, 3, 0, 0]}>
                              <LabelList dataKey="unique" position="top" style={{ fontSize: 9, fontWeight: 700, fill: '#0891b2' }} />
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    )}

                    {/* ── Who Visited — collapsible user list ── */}
                    <Box style={{ borderTop: `1px solid ${T.border}`, paddingTop: 2 }}>
                      <Box
                        onClick={() => setWhoOpen(o => !o)}
                        role="button"
                        aria-expanded={whoOpen}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          padding: '8px 0 6px', cursor: 'pointer', userSelect: 'none',
                        }}
                      >
                        <GroupIcon style={{ fontSize: '0.875rem', color: T.textDim }} />
                        <Typography style={{ fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: T.textDim, flex: 1 }}>
                          Who Visited
                        </Typography>
                        {!visitorUsersLoading && visitorUsers.length > 0 && (
                          <span style={{
                            fontSize: '0.625rem', fontWeight: 700, color: '#0891b2',
                            background: 'rgba(6,182,212,.1)', borderRadius: 10, padding: '2px 8px',
                            border: '1px solid rgba(6,182,212,.15)',
                          }}>
                            {visitorUsers.length}
                          </span>
                        )}
                        {whoOpen
                          ? <ExpandLessIcon style={{ fontSize: '0.875rem', color: T.textDim }} />
                          : <ExpandMoreIcon style={{ fontSize: '0.875rem', color: T.textDim }} />}
                      </Box>
                      <Collapse in={whoOpen} timeout={180}>
                        {visitorUsersLoading ? (
                          <Box style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0 10px' }}>
                            <CircularProgress size={12} style={{ color: '#06b6d4' }} />
                            <Typography style={{ color: T.textDim, fontSize: '0.75rem' }}>Loading…</Typography>
                          </Box>
                        ) : visitorUsers.length === 0 ? (
                          <Box style={{ color: T.textDim, fontSize: '0.8125rem', paddingBottom: 10 }}>No visitors in this period.</Box>
                        ) : (
                          <Box style={{ display: 'flex', flexDirection: 'column', gap: 0, paddingBottom: 6 }}>
                            {visitorUsers.map((u, idx) => {
                              const displayName = u.user_ref.includes('/') ? u.user_ref.split('/').pop()! : u.user_ref;
                              const userInitials = displayName.split('.').map((p: string) => p[0]?.toUpperCase() ?? '').join('').slice(0, 2);
                              const lastSeen = u.last_visit
                                ? new Date(u.last_visit).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                : '—';
                              const barPct = maxVisitsInUsers > 0 ? Math.max((u.visits / maxVisitsInUsers) * 100, 4) : 4;
                              const avatarColors = ['#0891b2', '#4f46e5', '#059669', '#d97706', '#dc2626', '#7c3aed'];
                              const avatarBg = avatarColors[idx % avatarColors.length];

                              return (
                                <Box
                                  key={u.user_ref}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    padding: '7px 10px', borderRadius: 8,
                                    transition: 'background .12s',
                                    borderBottom: idx < visitorUsers.length - 1 ? `1px solid ${T.border}` : 'none',
                                  }}
                                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = T.surfaceAlt; }}
                                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                >
                                  {/* Avatar */}
                                  <Box style={{
                                    width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                                    background: avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '0.5625rem', fontWeight: 700, color: '#fff',
                                    boxShadow: `0 1px 4px ${avatarBg}44`,
                                  }}>
                                    {userInitials || <PersonIcon style={{ fontSize: '0.75rem' }} />}
                                  </Box>

                                  {/* Name + ref */}
                                  <Box style={{ flex: '1 1 0', minWidth: 0 }}>
                                    <Typography style={{ fontSize: '0.75rem', fontWeight: 600, color: T.text, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {cleanName(displayName)}
                                    </Typography>
                                    {/* Mini visit bar */}
                                    <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                                      <Box style={{
                                        flex: '1 1 0', height: 3, borderRadius: 2, background: 'rgba(0,0,0,.05)', overflow: 'hidden', maxWidth: 100,
                                      }}>
                                        <Box style={{
                                          width: `${barPct}%`, height: '100%', borderRadius: 2,
                                          background: `linear-gradient(90deg, ${avatarBg}, ${avatarBg}99)`,
                                          transition: 'width .3s ease',
                                        }} />
                                      </Box>
                                      <Typography style={{ fontSize: '0.5625rem', color: T.textDim, whiteSpace: 'nowrap' }}>
                                        {u.visits} visit{u.visits !== 1 ? 's' : ''}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  {/* Visit count badge */}
                                  <Box style={{
                                    background: `${avatarBg}14`, border: `1px solid ${avatarBg}22`,
                                    borderRadius: 6, padding: '2px 7px', flexShrink: 0,
                                  }}>
                                    <Typography style={{ fontSize: '0.6875rem', fontWeight: 700, color: avatarBg, lineHeight: 1.3 }}>
                                      {u.visits}
                                    </Typography>
                                  </Box>

                                  {/* Last seen */}
                                  <Typography style={{ fontSize: '0.625rem', color: T.textDim, whiteSpace: 'nowrap', flexShrink: 0 }}>
                                    {lastSeen}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </Collapse>
                    </Box>
                  </>
                );
              })()}
            </Section>

            {/* ── Reactions Analytics ── */}
            <Section id="reactions" title="Reactions" count={Object.keys(reactionsData).length} open={exp.reactions} onToggle={() => flip('reactions')} flush>
              {reactionsLoading ? (
                <Box style={{ padding: 32, textAlign: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : Object.keys(reactionsData).length === 0 ? (
                <Box style={{ padding: '16px 18px 20px' }}><Box className={c.emptyState}>No reactions recorded yet.</Box></Box>
              ) : (() => {
                return (
                  <Table className={c.tbl}>
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ paddingLeft: 24 }}>Artifact</TableCell>
                        <TableCell style={{ width: 100 }}>Type</TableCell>
                        <TableCell align="center" style={{ width: 80 }}>
                          <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                            <ThumbUpIcon style={{ fontSize: 12, color: '#1565C0' }} /> Likes
                          </Box>
                        </TableCell>
                        <TableCell align="center" style={{ width: 80 }}>
                          <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                            <ThumbDownIcon style={{ fontSize: 12, color: '#C62828' }} /> Dislikes
                          </Box>
                        </TableCell>
                        <TableCell align="center" style={{ width: 70 }}>Total</TableCell>
                        <TableCell align="center" style={{ width: 70 }}>Net</TableCell>
                        <TableCell style={{ paddingRight: 24 }}>Who Reacted</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(reactionsData)
                        .sort(([, a], [, b]) => (b.upCount + b.downCount) - (a.upCount + a.downCount))
                        .map(([name, info]) => {
                          const net = info.upCount - info.downCount;
                          const total = info.upCount + info.downCount;
                          const artifactType = info.artifact_type as ItemType | undefined;
                          const meta = artifactType ? TYPE_META[artifactType] : undefined;
                          return (
                            <TableRow key={name}>
                              <TableCell style={{ paddingLeft: 24 }}>
                                <span style={{ fontWeight: 600, color: T.text }}>{cleanName(name)}</span>
                              </TableCell>
                              <TableCell>
                                {meta ? (
                                  <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: `${meta.color}18`, borderRadius: 10, padding: '2px 7px 2px 2px' }}>
                                    <Box style={{ width: 16, height: 16, borderRadius: '50%', background: meta.gradient, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                      <TypeIcon iconName={meta.iconName} style={{ color: '#fff', fontSize: '0.625rem' }} fontSize="inherit" />
                                    </Box>
                                    <span style={{ fontSize: '0.625rem', fontWeight: 700, color: meta.color, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>{meta.label}</span>
                                  </Box>
                                ) : (
                                  <span style={{ fontSize: '0.7rem', color: T.textSec }}>—</span>
                                )}
                              </TableCell>
                              <TableCell align="center" style={{ color: '#1565C0', fontWeight: 600 }}>
                                {info.upCount}
                              </TableCell>
                              <TableCell align="center" style={{ color: '#C62828', fontWeight: 600 }}>
                                {info.downCount}
                              </TableCell>
                              <TableCell align="center" style={{ fontWeight: 600, color: T.text }}>
                                {total}
                              </TableCell>
                              <TableCell align="center" style={{ fontWeight: 600, color: net > 0 ? '#1565C0' : net < 0 ? '#C62828' : T.textSec }}>
                                {net > 0 ? `+${net}` : net}
                              </TableCell>
                              <TableCell style={{ paddingRight: 24 }}>
                                {(() => {
                                  const MAX_INLINE = 3;
                                  const reactions = info.reactions;
                                  const showAll = reactions.length <= MAX_INLINE;
                                  const visible = showAll ? reactions : reactions.slice(0, MAX_INLINE);
                                  const remaining = reactions.length - MAX_INLINE;
                                  return (
                                    <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                                      {visible.map((r, i) => {
                                        const userName = cleanName(r.user.split('/').pop() ?? r.user);
                                        return (
                                          <Tooltip key={i} title={`${userName} — ${r.type === 'up' ? 'Liked' : 'Disliked'} · ${fmtShort(r.at)}`}>
                                            <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                              <Avatar style={{ width: 22, height: 22, fontSize: '0.5rem', fontWeight: 700, background: pickColor(userName), flexShrink: 0 }}>
                                                {initials(userName)}
                                              </Avatar>
                                              <Box style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                                <span style={{ fontSize: '0.80rem', fontWeight: 500, color: T.text }}>{userName}</span>
                                              </Box>
                                            </Box>
                                          </Tooltip>
                                        );
                                      })}
                                      {!showAll && (
                                        <Tooltip
                                          interactive
                                          title={
                                            <Box style={{ maxHeight: 200, overflowY: 'auto', padding: '4px 0' }}>
                                              {reactions.slice(MAX_INLINE).map((r, i) => {
                                                const uName = cleanName(r.user.split('/').pop() ?? r.user);
                                                return (
                                                  <Box key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 2px' }}>
                                                    <Avatar style={{ width: 18, height: 18, fontSize: '0.45rem', fontWeight: 700, background: pickColor(uName), flexShrink: 0 }}>
                                                      {initials(uName)}
                                                    </Avatar>
                                                    <span style={{ fontSize: '0.75rem' }}>
                                                      {uName} — {r.type === 'up' ? 'Liked' : 'Disliked'}
                                                    </span>
                                                  </Box>
                                                );
                                              })}
                                            </Box>
                                          }
                                        >
                                          <Box
                                            style={{
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: 4,
                                              background: T.surface,
                                              border: `1px solid ${T.border}`,
                                              borderRadius: 12,
                                              padding: '2px 8px',
                                              fontSize: '0.7rem',
                                              fontWeight: 600,
                                              color: T.textSec,
                                              cursor: 'pointer',
                                              whiteSpace: 'nowrap',
                                            }}
                                          >
                                            +{remaining} more
                                          </Box>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  );
                                })()}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                );
              })()}
            </Section>

            {/* Footer */}
            <Box className={c.footer}>
              <AssessmentIcon style={{ fontSize: '0.8125rem' }} />
              Verint Developer Portal · Analytics ·{' '}
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Export
   ═══════════════════════════════════════════════════════════════════════════ */
export function DashboardPage() {
  return (
    <MarketplaceProvider>
      <DashboardContent />
    </MarketplaceProvider>
  );
}
